/**
 * The sync engine. Provider-independent, by construction.
 *
 * There is no `if (provider === ...)` in this file, and no HTTP client, because
 * the engine's only knowledge of a provider is the `SyncProvider` it was handed.
 * Swapping Google Drive for a managed cloud is a change to which object the
 * registry constructs — not a change here. The contract suite proves that by
 * running this engine against both a managed and a file-storage provider with no
 * engine code varying between them.
 *
 * THE ORDER OF OPERATIONS IS THE WHOLE DESIGN
 * -------------------------------------------
 *   push → pull → validate → APPLY IN ONE TRANSACTION → advance cursor
 *
 * The cursor moves LAST, after the local commit has succeeded. Get this wrong —
 * advance the cursor as soon as the download succeeds — and a crash between the
 * download and the write silently skips every change in that batch. The data is
 * then permanently wrong, with no error anywhere, and the only symptom is an
 * invoice a customer says they never received.
 *
 * FAILURE IS NOT AN EXCEPTION THE USER SEES
 * ------------------------------------------
 * Every provider failure is caught and turned into a `SyncStatus`. A cloud that
 * is down must produce "Sync pending", not an unusable application: the user may
 * be in the middle of an invoice, and a billing app that stops billing because a
 * data centre elsewhere is rebooting has failed at its actual job.
 */
import { ensureBusinessChangeEnqueued } from './ensureBusinessChangeEnqueued';
import type { DNRDatabase } from '@data/db/database';
import type { SyncStateEntry } from '@data/db/types';
import type { SyncProvider, SkippedChange } from '@platform/cloud/types';
import { ProviderError } from '@platform/cloud/types';
import type { ProviderId } from '@domain/sync/changeModel';
import type { SyncStatus } from '@domain/sync/retention';
import { currentDeviceId } from './deviceIdentity';
import {
  failedCount as countFailed,
  failedEntries as getFailedEntries,
  markAcked,
  markAttemptFailed,
  pendingCount,
  retryFailedEntry as resetFailedEntry,
  takePending,
  toWireChange,
} from './outbox';
import { applyChanges } from './applyChanges';
import { acquireSyncLock } from './syncLock';
import { SUPPORT_REPUBLISH_BUSINESS_ID, SUPPORT_REPUBLISH_TARGETS } from './republishEntities';

function isSupportRecoveryTarget(
  businessId: string,
  entityType: string | undefined,
  entityId: string | undefined,
): boolean {
  if (businessId !== SUPPORT_REPUBLISH_BUSINESS_ID) return false;
  return SUPPORT_REPUBLISH_TARGETS.some(
    (target) => target.entityType === entityType && target.entityId === entityId,
  );
}

export type SyncOrigin = 'manual' | 'automatic' | 'initial_enable';

export interface SyncRunResult {
  readonly pushed: number;
  readonly pulled: number;
  readonly applied: number;
  readonly conflicts: number;
  /** Changes that were permanently rejected by the provider and are not retried automatically. */
  readonly failed: number;
  /**
   * Downloaded changes this device could not read or apply.
   *
   * Never silently zero: the engine cannot apply these, and the device must be
   * able to say so instead of looking current while missing records.
   */
  readonly skipped: number;
  readonly recovered: boolean;
  /** What initiated this run. */
  readonly origin: SyncOrigin;
  readonly status: SyncStatus;
}

export interface SyncEngineDeps {
  readonly db: DNRDatabase;
  readonly provider: SyncProvider;
  readonly now?: () => string;
  /**
   * Which device this engine speaks for.
   *
   * Defaults to the install's own device. It is injectable because a second
   * device is genuinely a second install, and a test cannot create one inside a
   * single IndexedDB — without this, two engines share a device id, share a
   * cursor, and the "second device" is really just the first one again, so
   * every multi-device test silently proves nothing.
   *
   * Production code should leave it unset.
   */
  readonly deviceId?: string;
}

/** Status the settings screen renders. Never throws, never blocks the app. */
export async function statusFor(
  db: DNRDatabase,
  businessId: string,
  deviceId: string,
  providerId: ProviderId | null,
  state: SyncStateEntry,
  conflicts: number,
): Promise<SyncStatus> {
  const [pending, failed, entries] = await Promise.all([
    pendingCount(db, businessId),
    countFailed(db, businessId),
    getFailedEntries(db, businessId),
  ]);
  // Determine the most recent error from failed entries.
  const failedError =
    entries.length > 0 ? (entries[entries.length - 1]?.lastError ?? undefined) : undefined;
  return {
    kind:
      failed > 0
        ? 'error'
        : state.state === 'recovering'
          ? 'recovering'
          : state.state === 'error'
            ? 'error'
            : pending > 0
              ? 'pending'
              : conflicts > 0
                ? 'conflict'
                : 'synced',
    businessId,
    providerId,
    deviceId,
    pendingCount: pending,
    failedCount: failed,
    lastSuccessfulAt: state.lastSuccessfulSyncAt ?? null,
    lastError: state.lastError,
    failedError,
      unresolvedConflictCount: conflicts,
    recovering: state.state === 'recovering',
    lastSyncOrigin: state.lastSyncOrigin ?? null,
    skippedChangeCount: state.lastSkippedCount,
    skippedChangeReasons: state.lastSkippedReasons,
    skippedChangeAt: state.lastSkippedAt,
  };
}

export class SyncEngine {
  private readonly db: DNRDatabase;
  private readonly provider: SyncProvider;
  private readonly now: () => string;
  private readonly fixedDeviceId?: string;

  constructor(deps: SyncEngineDeps) {
    this.db = deps.db;
    this.provider = deps.provider;
    this.now = deps.now ?? (() => new Date().toISOString());
    this.fixedDeviceId = deps.deviceId;
  }

  private async deviceId(): Promise<string> {
    return this.fixedDeviceId ?? (await currentDeviceId(this.db));
  }

  private stateId(businessId: string, deviceId: string): string {
    return `${businessId}:${deviceId}:${this.provider.id}`;
  }

  private async readState(businessId: string, deviceId: string): Promise<SyncStateEntry> {
    const id = this.stateId(businessId, deviceId);
    const existing = await this.db.syncState.get(id);
    if (existing) return existing;
    const fresh: SyncStateEntry = {
      id,
      businessId,
      deviceId,
      providerId: this.provider.id,
      state: 'idle',
    };
    await this.db.syncState.put(fresh);
    return fresh;
  }

  private async writeState(
    businessId: string,
    deviceId: string,
    patch: Partial<SyncStateEntry>,
  ): Promise<void> {
    const id = this.stateId(businessId, deviceId);
    const current = await this.db.syncState.get(id);
    if (!current) return;
    await this.db.syncState.put({ ...current, ...patch });
  }

  /** The status the settings screen renders. Never throws. */
  async status(businessId: string): Promise<SyncStatus> {
    const deviceId = await this.deviceId();
    const state = await this.readState(businessId, deviceId);
    const conflicts = await this.db.syncConflicts
      .where('businessId')
      .equals(businessId)
      .filter((c) => !c.resolved)
      .count();
    return statusFor(this.db, businessId, deviceId, this.provider.id, state, conflicts);
  }

  /**
   * Reset a permanently-failed outbox entry back to `pending` so the next sync
   * run will retry it.
   *
   * This is a manual user action exposed through the settings UI. Entries
   * marked `failed` are not retried automatically because the provider said the
   * rejection was permanent (non-retryable). The user may retry if they believe
   * the underlying issue has been resolved (e.g. permissions fixed, data
   * corrected, or a transient server error cleared).
   *
   * Only entries already in `failed` status are affected; `pending`, `acked`,
   * or non-existent changeIds are silently ignored.
   */
  async retryFailed(businessId: string, changeId: string): Promise<void> {
    await resetFailedEntry(this.db, changeId);
  }

  /**
   * One full cycle: push, then pull, then apply, then advance the cursor.
   *
   * Push happens FIRST so a device that is behind publishes what it knows before
   * asking what it missed. The other order means a device pulls, applies, then
   * pushes changes derived from a stale view — two devices can then each apply
   * the other's stale version in turn and never converge.
   *
   * Every failure below is caught. A provider outage returns a status, not an
   * exception, because the caller is a timer.
   */
  async run(businessId: string, origin: SyncOrigin = 'automatic'): Promise<SyncRunResult> {
    // The lock covers EVERYTHING the cycle reads and writes, and the release is
    // in a `finally` around all of it — including the device identity and the
    // stored position.
    //
    // Those two reads used to sit ABOVE the `try`. A database error there
    // rejected the call while the lock was held, and nothing releases a lock a
    // failed call took: every later run for this business — the button and the
    // 30-second poll alike — waited on it forever. The UI spun on "Syncing…"
    // and only a page reload recovered the app.
    const release = await acquireSyncLock(businessId);
    try {
      const deviceId = await this.deviceId();
      const state = await this.readState(businessId, deviceId);
      return await this.runCycle(businessId, deviceId, state, origin);
    } finally {
      release();
    }
  }

  /**
   * One cycle, with the lock already held.
   *
   * Every provider failure is caught and recorded in the state; the result is
   * returned rather than thrown, so a timer and a button can both report what
   * happened without a try/catch of their own.
   */
  private async runCycle(
    businessId: string,
    deviceId: string,
    state: SyncStateEntry,
    origin: SyncOrigin,
  ): Promise<SyncRunResult> {
    let pushed = 0;
    let failed = 0;
    let applied = 0;
    let conflicts = 0;
    let recovered = false;
    let skipped = 0;
    let pushError: string | undefined;

    try {
      await this.writeState(businessId, deviceId, { state: 'syncing' });
      const pushResult = await this.push(businessId, deviceId);
      pushed = pushResult.sent;
      failed = pushResult.failed;
      // If there were non-retryable push failures, record the error so the
      // state transitions to 'error' instead of 'idle'. Without this, a failed
      // push is silently swallowed by the catch-free path below.
      if (failed > 0) {
        pushError = `Push had ${failed} permanent failure(s). See the failed changes list for details.`;
      }
      const pullResult = await this.pull(businessId, deviceId, state);
      applied = pullResult.applied;
      conflicts = pullResult.conflicts;
      recovered = pullResult.recovered;
      // A downloaded change that could not be read is reported, never dropped on
      // the floor. The rows stay in the cloud, so this fact outlives the cycle
      // that found them: a later clean pull does not mean they were applied.
      const skippedReasons = [
        ...pullResult.skipped.map((s) => `${s.changeId ?? 'unknown'}: ${s.reason}`),
        ...pullResult.rejected,
      ];
      skipped = skippedReasons.length;
      const skippedPatch =
        skipped > 0
          ? {
              lastSkippedCount: skipped,
              lastSkippedReasons: skippedReasons.slice(0, 3),
              lastSkippedAt: this.now(),
            }
          : {};
      const recoveryIncomplete = pullResult.rejected.some((reason) =>
        reason.includes('recovery target '),
      );
      // A rejected push does not get to claim a successful cycle — and it does
      // not get to ERASE the last one either. Writing `lastSuccessfulSyncAt:
      // undefined` on that branch made the panel say "Last successful sync:
      // Never" after a failed run that followed a good one.
      await this.writeState(
        businessId,
        deviceId,
        pushError || recoveryIncomplete
          ? {
              state: 'error',
              lastError: pushError ?? 'Sync did not apply every approved recovery target.',
              ...skippedPatch,
            }
          : {
              state: 'idle',
              lastSuccessfulSyncAt: this.now(),
              lastError: undefined,
              lastSyncOrigin: origin,
              ...skippedPatch,
            },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await this.writeState(businessId, deviceId, { state: 'error', lastError: message });
    }

    return {
      pushed,
      pulled: applied,
      applied,
      conflicts,
      failed,
      skipped,
      recovered,
      origin,
      status: await this.status(businessId),
    };
  }

  /**
   * Send the outbox, in batches the provider says it can take.
   *
   * The batch size comes from the provider's CAPABILITIES, not a constant. A
   * file-storage provider that accepts 25 changes will reject a 500-item array,
   * and a hardcoded 500 would mean the engine only ever works against one kind
   * of cloud — which is exactly the assumption this architecture exists to
   * avoid.
   */
  private async push(
    businessId: string,
    deviceId: string,
  ): Promise<{ sent: number; failed: number }> {
    const maxBatch = Math.max(1, this.provider.capabilities().maxBatchSize);
    let sent = 0;
    let failed = 0;

    // Ensure a business entity change is in the outbox. This is always called,
    // regardless of whether other pending entries exist, because:
    // 1. For fresh businesses, the business change must be enqueued.
    // 2. For businesses whose change was acked but the cursor was later rolled
    //    back, the outbox entry may be gone but the server might lack the
    //    business change (e.g. after a migration cleaned malformed entries).
    // The function is idempotent: if a valid business change already exists,
    // nothing is added.
    await ensureBusinessChangeEnqueued(this.db, businessId);

    for (;;) {
      const batch = await takePending(this.db, businessId, maxBatch);
      if (batch.length === 0) break;

      // Guard against a batch that can never be acked. Without this, one
      // permanently-rejected change would loop here forever, since it stays
      // `pending` or flips to `failed` and is re-read next pass.
      //
      // `lastAttemptRetryable` is required, not decoration. `retryCount` counts
      // ATTEMPTS, and an unreachable cloud increments it exactly like a refusal
      // does — so on the counter alone, a four-minute outage looked like a
      // permanent verdict at the eighth poll. The ninth attempt was refused
      // here, without asking the provider, and the manual retry the screen
      // offers then hit the same guard forever. Only an attempt the provider
      // explicitly said is not worth repeating may end an entry's life.
      const only = batch[0];
      if (
        batch.length === 1 &&
        only &&
        only.retryCount >= MAX_ATTEMPTS &&
        only.lastAttemptRetryable === false
      ) {
        await markAttemptFailed(
          this.db,
          only.changeId,
          'gave up after repeated failures',
          false,
          this.now(),
        );
        failed += 1;
        break;
      }

      try {
        const result = await this.provider.pushChanges({
          businessId,
          deviceId,
          changes: batch.map(toWireChange),
        });

        // Acknowledge in a transaction of its own, after the provider confirmed.
        // The cursor is not touched here — that happens in pull(), after apply.
        await markAcked(this.db.syncOutbox, result.accepted);

        // Anything the provider did NOT accept stays pending for the next run.
        for (const entry of batch) {
          if (!result.accepted.includes(entry.changeId)) {
            await markAttemptFailed(
              this.db,
              entry.changeId,
              'provider did not acknowledge this change',
              true,
              this.now(),
            );
          }
        }
        sent += result.accepted.length;

        // Guard against a provider that returns an empty acceptance list
        // without throwing. Without this, the batch is re-read by takePending
        // (entries stay pending), retried with the same result, and the loop
        // never terminates — the engine appears to "hang" on Sync Now.
        if (result.accepted.length === 0) break;
      } catch (error) {
        const retryable = !(error instanceof ProviderError) || error.retryable;
        let newlyFailed = 0;
        for (const entry of batch) {
          await markAttemptFailed(
            this.db,
            entry.changeId,
            error instanceof Error ? error.message : String(error),
            retryable,
            this.now(),
          );
          if (!retryable) newlyFailed += 1;
        }
        failed += newlyFailed;
        // A failure here is not fatal to the run: the pending changes are
        // recorded with their error, and the PULL half may still succeed. A
        // device that can receive but not send is still useful.
        break;
      }
    }

    return { sent, failed };
  }

  /**
   * Fetch, apply, and only then advance the cursor.
   *
   * The long-offline path is here: when the provider says the cursor is older
   * than the retained log, a snapshot is fetched instead. See `recover`.
   */
  private async pull(
    businessId: string,
    deviceId: string,
    state: SyncStateEntry,
  ): Promise<{
    applied: number;
    conflicts: number;
    recovered: boolean;
    /** Rows the provider refused to hand over. */
    skipped: readonly SkippedChange[];
    /** Rows that reached `applyChanges` and were refused there. */
    rejected: readonly string[];
  }> {
    const cursor = state.cursor ? { token: state.cursor } : null;
    const result = await this.provider.pullChanges({
      businessId,
      deviceId,
      cursor,
      limit: this.provider.capabilities().maxBatchSize,
    });

    if (result.requiresSnapshot) {
      return this.recover(businessId, deviceId);
    }

    if (result.changes.length === 0) {
      const skippedRecoveryTargets = result.skipped.filter((change) =>
        isSupportRecoveryTarget(businessId, change.entityType, change.entityId),
      );
      if (skippedRecoveryTargets.length > 0) {
        return {
          applied: 0,
          conflicts: 0,
          recovered: false,
          skipped: result.skipped,
          rejected: skippedRecoveryTargets.map(
            (change) =>
              `${change.changeId ?? 'unknown'}: recovery target ${change.entityType} ` +
              `${change.entityId} was not readable (${change.reason})`,
          ),
        };
      }
      // A page with no READABLE changes is not the same as an empty page, and
      // the difference decides whether this device ever moves again.
      //
      // When every row was refused, the provider still told us where the page
      // ended. Returning here without recording that leaves the cursor where it
      // was, so the very same unreadable page is fetched on every poll, forever:
      // a silent permanent stall that looks exactly like "nothing to do".
      //
      // The cursor may move only when something was actually refused — a page
      // the server genuinely had nothing for must still leave the position
      // alone — and only to the end the server described, never past it.
      if (result.skipped.length > 0 && result.nextCursor) {
        await this.writeState(businessId, deviceId, { cursor: result.nextCursor.token });
        await this.provider.acknowledge({
          businessId,
          deviceId,
          cursor: result.nextCursor,
        });
      }
      return { applied: 0, conflicts: 0, recovered: false, skipped: result.skipped, rejected: [] };
    }

    const applyResult = await applyChanges({
      db: this.db,
      businessId,
      deviceId,
      changes: result.changes,
      now: this.now(),
      fromSnapshot: false,
    });

    const skippedRecoveryChanges = applyResult.skippedChanges.filter((change) =>
      isSupportRecoveryTarget(change.businessId, change.entityType, change.entityId),
    );
    const rejectedRecoveryChanges = result.changes.filter(
      (change) =>
        isSupportRecoveryTarget(change.businessId, change.entityType, change.entityId) &&
        applyResult.rejected.some((rejected) => rejected.startsWith(`${change.changeId}:`)),
    );
    const skippedRecoveryProviderChanges = result.skipped.filter((change) =>
      isSupportRecoveryTarget(businessId, change.entityType, change.entityId),
    );
    if (
      skippedRecoveryChanges.length > 0 ||
      rejectedRecoveryChanges.length > 0 ||
      skippedRecoveryProviderChanges.length > 0
    ) {
      return {
        applied: applyResult.applied,
        conflicts: applyResult.conflicts.length,
        recovered: false,
        skipped: result.skipped,
        rejected: [
          ...applyResult.rejected,
          ...skippedRecoveryChanges.map(
            (change) =>
              `${change.changeId}: recovery target ${change.entityType} ${change.entityId} ` +
              'was skipped while applying; cursor was not advanced',
          ),
          ...rejectedRecoveryChanges.map(
            (change) =>
              `${change.changeId}: recovery target ${change.entityType} ${change.entityId} ` +
              'was rejected while applying; cursor was not advanced',
          ),
          ...skippedRecoveryProviderChanges.map(
            (change) =>
              `${change.changeId ?? 'unknown'}: recovery target ${change.entityType} ` +
              `${change.entityId} was unreadable; cursor was not advanced`,
          ),
        ],
      };
    }

    // THE CURSOR ADVANCES ONLY NOW.
    //
    // Everything above can fail or be interrupted; the local rows are already
    // committed, so it is now safe to record how far we have got. Reversing
    // these two operations would skip the batch on any crash between them.
    if (result.nextCursor) {
      await this.writeState(businessId, deviceId, { cursor: result.nextCursor.token });
    }
    await this.provider.acknowledge({
      businessId,
      deviceId,
      cursor: result.nextCursor ?? { token: state.cursor ?? '' },
    });

    return {
      applied: applyResult.applied,
      conflicts: applyResult.conflicts.length,
      recovered: false,
      skipped: result.skipped,
      // `applyChanges` refuses a change it cannot validate and keeps going. That
      // refusal is carried up too: a row a provider vouched for and this layer
      // still could not use must not disappear between the two.
      rejected: applyResult.rejected,
    };
  }

  /**
   * LONG-OFFLINE RECOVERY
   * --------------------
   * A device whose cursor is older than the retained log cannot be caught up
   * incrementally — the changes it needs no longer exist. The alternative,
   * resending the whole history, is not available either: the provider compacted
   * it precisely because every active device had moved past it.
   *
   * So: take a snapshot, and be explicit about what that costs.
   *
   * WHAT HAPPENS TO LOCAL CHANGES MADE WHILE OFFLINE
   * -------------------------------------------------
   * They are NOT discarded, and this is the important part.
   *
   * `run()` PUSHES before it pulls, so anything written while offline has
   * already been published to the provider before recovery begins. The snapshot
   * therefore contains this device's own work, and re-applying it is a no-op
   * rather than a rollback.
   *
   * The residual case is a change the provider rejected while offline. That
   * change is still in the outbox as `pending` or `failed`; the snapshot does not
   * touch it, so it is retried on the next run and, if it is still failing, it
   * is surfaced as a conflict rather than quietly overwritten. Nothing is lost
   * in either direction.
   *
   * WHY THE CURSOR IS SET TO THE SNAPSHOT'S, NOT KEPT
   * --------------------------------------------------
   * Keeping a cursor older than the compacted log would guarantee the next pull
   * asks for the same impossible window and recovers again, forever. The
   * snapshot's cursor is the truth as of the moment it was taken, so it is the
   * only position from which incremental sync is meaningful again.
   */
  private async recover(
    businessId: string,
    deviceId: string,
  ): Promise<{
    applied: number;
    conflicts: number;
    recovered: boolean;
    skipped: readonly SkippedChange[];
    rejected: readonly string[];
  }> {
    await this.writeState(businessId, deviceId, { state: 'recovering' });

    const snapshot = await this.provider.fetchSnapshot({ businessId });

    // PRE-FLIGHT: the snapshot must carry a business row for this id. This
    // mirrors the guard in `cloudRestore.ts` and prevents a pruned or
    // scrambled business change from silently advancing the cursor while
    // leaving the local database without an active business row.
    const businessChange = snapshot.changes.find(
      (c) => c.entityType === 'business' && c.entityId === businessId && c.businessId === businessId,
    );
    if (!businessChange) {
      throw new ProviderError(
        'REJECTED',
        'The synced business snapshot has no business row.',
        false,
      );
    }

    const applyResult = await applyChanges({
      db: this.db,
      businessId,
      deviceId,
      changes: snapshot.changes,
      now: this.now(),
      // Conflicts are not raised during recovery. A device must always be able
      // to come back, even one whose local state disagrees with the cloud.
      fromSnapshot: true,
    });

    const skippedRecoveryChanges = applyResult.skippedChanges.filter((change) =>
      isSupportRecoveryTarget(change.businessId, change.entityType, change.entityId),
    );
    const rejectedRecoveryChanges = snapshot.changes.filter(
      (change) =>
        isSupportRecoveryTarget(change.businessId, change.entityType, change.entityId) &&
        applyResult.rejected.some((rejected) => rejected.startsWith(`${change.changeId}:`)),
    );
    const skippedRecoveryProviderChanges = snapshot.skipped.filter((change) =>
      isSupportRecoveryTarget(businessId, change.entityType, change.entityId),
    );
    if (
      skippedRecoveryChanges.length > 0 ||
      rejectedRecoveryChanges.length > 0 ||
      skippedRecoveryProviderChanges.length > 0
    ) {
      return {
        applied: applyResult.applied,
        conflicts: applyResult.conflicts.length,
        recovered: false,
        skipped: snapshot.skipped,
        rejected: [
          ...applyResult.rejected,
          ...skippedRecoveryChanges.map(
            (change) =>
              `${change.changeId}: recovery target ${change.entityType} ${change.entityId} ` +
              'was skipped while applying the snapshot; cursor was not advanced',
          ),
          ...rejectedRecoveryChanges.map(
            (change) =>
              `${change.changeId}: recovery target ${change.entityType} ${change.entityId} ` +
              'was rejected while applying the snapshot; cursor was not advanced',
          ),
          ...skippedRecoveryProviderChanges.map(
            (change) =>
              `${change.changeId ?? 'unknown'}: recovery target ${change.entityType} ` +
              `${change.entityId} was unreadable in the snapshot; cursor was not advanced`,
          ),
        ],
      };
    }

    await this.writeState(businessId, deviceId, { cursor: snapshot.cursor.token });
    await this.provider.acknowledge({ businessId, deviceId, cursor: snapshot.cursor });

    return {
      applied: applyResult.applied,
      conflicts: 0,
      recovered: true,
      skipped: snapshot.skipped,
      rejected: applyResult.rejected,
    };
  }
}

/**
 * How many times one change is retried before it is marked permanently failed.
 *
 * Bounded on purpose. An unbounded retry loop against a provider that rejects a
 * specific change means the pending count never drops, the user sees a permanent
 * warning they cannot explain, and every sync run re-sends a payload that will
 * be rejected again.
 */
const MAX_ATTEMPTS = 8;
